import React, { useEffect, useState } from 'react'
import * as S from './uploadFileModalWindow.style'
import { v4 as uuidv4 } from 'uuid'
import { updateColaFileKnowledgeSources } from '../../../api/websiteKnowledgeBase'
import { alertError } from 'api'
import { ONE_MB } from 'constants/dataSize'
import LoaderOverlay from '../../../../../uiKit/loaders/loaderOverlay'
import { UploadButton } from './button/upload-button'
import { UploadFileTable } from './table/upload-files-table'

export interface FileData {
  _id: any
  file: File
  originalFilename: string
  description: string
}

interface FileUploadProps {
  listNames: string[]
  updateTrainedFilesCallBack: any
}

export const FileUpload: React.FC<FileUploadProps> = ({ updateTrainedFilesCallBack, listNames }) => {
  const [filesData, setFilesData] = useState<FileData[]>([])
  const [isUploading, setIsUploading] = useState(false)
  const [notTrainedFileLimitAchieved, setNotTrainedFileLimitAchieved] = useState(true)

  useEffect(() => {
    setNotTrainedFileLimitAchieved(filesData.length >= NOT_TRAINED_FILE_LIMIT)
  }, [filesData])

  const FILE_SIZE_LIMIT = ONE_MB * 100
  const NOT_TRAINED_FILE_LIMIT = 10

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return
    const validFiles = validateFiles([...e.target.files])
    if (validFiles.length > 0) {
      const selected = validFiles.map(file => ({
        _id: uuidv4(),
        file,
        description: '',
        originalFilename: file.name,
      }))
      setFilesData(prev => [...prev, ...selected])
    }
  }

  const handleDescriptionChange = (id: string, value: string) => {
    setFilesData(prev => {
      const updated = [...prev]
      updated.find(file => file._id == id).description = value
      return updated
    })
  }

  const handleDelete = (id: string) => {
    setFilesData(prevSources => prevSources.filter(x => x._id !== id))
  }

  const handleUpload = () => {
    const formData = prepareFormData(filesData)
    setIsUploading(true)
    updateColaFileKnowledgeSources(formData)
      .then(() => {
        setFilesData([])
        updateTrainedFilesCallBack()
        setIsUploading(false)
      })
      .catch(error => {
        alertError(error?.message || 'Something went wrong')
        setIsUploading(false)
      })
  }

  const prepareFormData = (files: FileData[]) => {
    const formData = new FormData()
    files.forEach(file => {
      formData.append('files', file.file)
    })
    const descriptions = files.map(file => ({ description: file.description }))
    const descriptionsBlob = new Blob([JSON.stringify(descriptions)], { type: 'application/json' })
    formData.append('descriptions', descriptionsBlob)
    return formData
  }

  const validateFiles = (files: File[]) => {
    const validFiles = files.filter(file => isValidFile(file))
    const takenFiles = filterFilesWithinSizeLimit(validFiles)
    if (takenFiles.length < validFiles.length) {
      alertError('The total size of the files is too big.')
    }
    return takenFiles.slice(0, getFileUploadingLimit())
  }

  const getFileUploadingLimit = () => NOT_TRAINED_FILE_LIMIT - filesData.length

  const isValidFile = file => {
    const isNotUniqueFile =
      filesData.some(fileData => fileData.originalFilename === file?.name) || listNames.some(name => name === file?.name)
    const isTooBig = file?.size > FILE_SIZE_LIMIT
    if (isNotUniqueFile) {
      alertError(`File '${file?.name}' is already added.`)
      return false
    } else if (isTooBig) {
      alertError(`File '${file?.name}' is too big to upload.`)
      return false
    }
    return true
  }

  const filterFilesWithinSizeLimit = (files: File[]) => {
    let totalByteSize = 0
    return files.filter(file => {
      const mayBeAdded = totalByteSize + file.size <= FILE_SIZE_LIMIT
      if (mayBeAdded) {
        totalByteSize += file.size
      }
      return mayBeAdded
    })
  }

  const hasEmptyDescription = filesData.some(item => !item.description.trim())

  return (
    <S.UploadForm>
      {isUploading && (
        <>
          <LoaderOverlay />
        </>
      )}
      <UploadButton
        notTrainedFileLimitAchieved={notTrainedFileLimitAchieved}
        onChange={handleFileChange}
        onClick={event => {
          event.target.value = null
        }}
      />
      {filesData.length > 0 ? (
        <>
          <UploadFileTable files={filesData} onDelete={handleDelete} onBlurHandle={handleDescriptionChange} />
          <S.ButtonWrapper>
            {!hasEmptyDescription ? (
              <S.Button onClick={handleUpload}>{'Upload & train'}</S.Button>
            ) : (
              <S.DisabledButton>{'Upload & train'}</S.DisabledButton>
            )}
          </S.ButtonWrapper>
        </>
      ) : (
        <S.PreviewWrapper>
          <p style={{ color: 'var(--color-pale-sky)', marginBottom: '40px' }}>Files should be PDF or Docx</p>
        </S.PreviewWrapper>
      )}
    </S.UploadForm>
  )
}
